import { AgGridAngular } from "@ag-grid-community/angular";
import { GridOptions, ICellRendererParams, ValueFormatterParams, ValueGetterParams } from "@ag-grid-community/core";
import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal, ViewChild } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  AccountMarketplace,
  DspAdgroup,
  DspAdvertiser,
  DspCampaign,
  DspCampaignStateEnum,
  DspCreative,
  DspDeliveryStatus,
  DspStats,
} from "@front/m19-api-client";
import { getFilteredLeafNodes } from "@front/m19-grid-config";
import { Metric } from "@front/m19-metrics";
import {
  AccountMarketplaceService,
  AccountSelectionService,
  AuthService,
  DspService,
  MetricsSelectorPage,
  MetricsSelectorKey,
  StatsService,
  UserSelectionService,
} from "@front/m19-services";
import {
  ActionButton,
  ActionButtonsComponent,
  ChartConfig,
  IButtonDirective,
  IChartComponent,
  IIconComponent,
  ModalOptions,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { DateAggregation, emptyDspStats, isEmptyDspStats, sumDspStats } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { ChartLayoutComponent } from "@m19-board/containers/chart-layout/chart-layout.component";
import { GridLayoutComponent } from "@m19-board/containers/grid-layout/grid-layout.component";
import { actionColumnProperties } from "@m19-board/grid-config/grid-columns";
import { getCsvFileName, restoreDefaultColumns } from "@m19-board/grid-config/grid-config";
import { MetricSelectorComponent } from "@m19-board/insights/metric-selector/metric-selector.component";
import { DSP_COST, DSP_SALES, DspDisplayMetrics } from "@m19-board/models/DspMetrics";
import { LoadingService } from "@m19-board/services/loading.service";
import { OverdueAmazonInvoiceWarningComponent } from "@m19-board/settings/amazon-invoices/overdue-amazon-invoice-warning.component";
import {
  ChartData,
  ChartModalComponent,
  ChartModalInputData,
} from "@m19-board/shared/chart-modal/chart-modal.component";
import {
  CompactFilterComponent,
  Filter,
  FilterCategory,
} from "@m19-board/shared/compact-filter/compact-filter.component";
import { CompactFilterService } from "@m19-board/shared/compact-filter/compact-filter.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  filter,
  map,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  Subject,
  switchMap,
  tap,
} from "rxjs";
import { DspCampaignState, DspCreativeStats, DspGridColumnsService, dspGridOptions } from "./dsp-grid-columns.service";
import {
  DspStatsLineItemDetailsModalComponent,
  DspStatsLineItemDetailsModalData,
} from "./dsp-stats-line-item-details-modal.component";

@Component({
  selector: "app-dsp-stats",
  templateUrl: "./dsp-stats.component.html",
  imports: [
    IButtonDirective,
    MetricSelectorComponent,
    AgGridAngular,
    TranslocoDirective,
    ChartLayoutComponent,
    GridLayoutComponent,
    CompactFilterComponent,
    OverdueAmazonInvoiceWarningComponent,
    IIconComponent,
    IChartComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class DspStatsComponent implements OnInit {
  private readonly dspColumns = inject(DspGridColumnsService);
  private readonly loadingService = inject(LoadingService);
  private readonly compactFilterService = inject(CompactFilterService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly accountMarketplaceService = inject(AccountMarketplaceService);
  private readonly statsService = inject(StatsService);
  private readonly toastrService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly authService = inject(AuthService);
  private readonly dspService = inject(DspService);
  private readonly modalService = inject(ModalService);

  readonly dspMetrics: Metric<DspStats>[] = DspDisplayMetrics; // Metrics to display on the selector
  readonly localstorageKey: MetricsSelectorKey = { page: MetricsSelectorPage.dspStats };

  accountMarketplace?: AccountMarketplace;
  dspAdvertiser$ = new Subject<DspAdvertiser | undefined>();
  dspAdvertiser = toSignal<DspAdvertiser | undefined>(this.dspAdvertiser$);
  dspAdgroups: Map<string, DspAdgroup> = new Map();
  filter$: Subject<{ dspOrderIds?: string[]; dspCreativeIds?: string[] }> = new BehaviorSubject({});

  readonly dataLoading = signal(true);
  readonly globalChartDisplay = signal<boolean>(
    this.userSelectionService.getUserChartDisplayedPreference(this.localstorageKey),
  );

  readonly totalData = signal<DspStats>(emptyDspStats());
  readonly previousTotalData = signal<DspStats>(emptyDspStats());
  readonly globalDatasetData = signal<DspStats[]>([]);

  // AG Grid - campaign stats
  @ViewChild("campaignStatsGrid") campaignStatsGrid!: AgGridAngular;
  readonly campaignStatsGridData = signal<DspCampaignStats[]>([]);
  private previousCampaignStatsGridData = new Map<string, DspStats>();
  private campaignStats$ = new ReplaySubject<Map<string, ChartData<DspStats>>>(1);
  private previousCreativeStatsGridData = new Map<string, DspStats>();
  private creativeStats$ = new ReplaySubject<Map<string, ChartData<DspStats>>>(1);

  readonly campaignStatsGridOptions: GridOptions<DspCampaignStats> = {
    ...dspGridOptions<DspCampaignStats>({ gridKey: "dsp_campaign_stats" }),
    columnDefs: [
      {
        headerName: this.translocoService.translate("dsp-stats.order", {}, "en"),
        headerValueGetter: (params) => this.translocoService.translate("dsp-stats.order"),
        field: "dspCampaignName",
        floatingFilter: true,
        filter: "agTextColumnFilter",
        pinned: "left",
        valueFormatter: (params: ValueFormatterParams<DspCampaignStats, string>) => {
          if (params.node?.isRowPinned()) {
            return "";
          }
          return params.value ?? "Unknown";
        },
      },
      {
        headerName: this.translocoService.translate("dsp-stats.state", {}, "en"),
        headerValueGetter: (params) => this.translocoService.translate("dsp-stats.state"),
        field: "state",
        pinned: "left",
        valueFormatter: (params: ValueFormatterParams<DspCampaignStats, DspCampaignStateEnum>) =>
          params.value ? this.translocoService.translate(DspCampaignState[params.value]) : "",
        floatingFilter: true,
        filter: "agSetColumnFilter",
        filterValueGetter: (params: ValueGetterParams<DspCampaignStats, DspCampaignStateEnum>) =>
          params.data?.state ? this.translocoService.translate(DspCampaignState[params.data.state]) : "",
      },
      this.dspColumns.deliveryStatusColumn<DspCampaignStats>(),
      ...this.dspColumns.metricsColumns<DspCampaignStats>((params) =>
        params.node.isRowPinned() || !params.data
          ? undefined
          : this.previousCampaignStatsGridData.get(params.data.orderId!),
      ),
      {
        ...actionColumnProperties<DspCampaignStats, string>(),
        cellRendererSelector: (params) => {
          const title = params.node.isRowPinned()
            ? this.translocoService.translate("dsp-stats.dsp_order_stats_graph_total")
            : ((this.translocoService.translate("dsp-stats.dsp_order_stats_graph") +
                " - " +
                params.data?.dspCampaignName) as string);
          const btns: ActionButton[] = [
            {
              icon: "chartLine",
              tooltip: params.node.isRowPinned()
                ? this.translocoService.translate("dsp-stats.display_dsp_order_stats_graph_total")
                : this.translocoService.translate("dsp-stats.display_dsp_order_stats_graph"),
              onClick: (params: ICellRendererParams) => {
                const orderIds = getFilteredLeafNodes(params.node, this.campaignStatsGrid.api).map(
                  (n) => n.data.orderId as string,
                );
                this.openChartModal(orderIds, title, this.campaignStats$);
              },
            },
            {
              icon: "list",
              tooltip: this.translocoService.translate("dsp-stats.display_dsp_order_stats_details"),
              onClick: (params: ICellRendererParams) => {
                const nodes = getFilteredLeafNodes(params.node, this.campaignStatsGrid.api);
                const lineItemsData = nodes.flatMap((n) => n.data.lineItemStats as DspStats[]);
                const title =
                  nodes.length > 1
                    ? this.translocoService.translate("dsp-stats.line_item_details")
                    : this.translocoService.translate("dsp-stats.line_item_details_order", {
                        order: nodes[0].data.dspCampaignName ?? "unknown",
                      });
                this.openLineItemDetailsModal(title, lineItemsData);
              },
            },
          ];
          return {
            component: ActionButtonsComponent,
            params: {
              actionButtons: btns,
            },
          };
        },
      },
      this.dspColumns.currencyColumn<DspCampaignStats>(),
    ],
    getRowId: (row) => row.data.orderId ?? "",
  };

  // AG Grid - creative stats
  @ViewChild("creativeStatsGrid") creativeStatsGrid!: AgGridAngular;
  readonly creativeStatsGridData = signal<DspCreativeStats[]>([]);
  readonly creativeStatsGridOptions: GridOptions<DspCreativeStats> = {
    ...dspGridOptions<DspCreativeStats>({ gridKey: "dsp_creative_stats" }),
    columnDefs: [
      this.dspColumns.creativeNameColumn<DspCreativeStats>(),
      this.dspColumns.creativeTypeColumn<DspCreativeStats>({
        colId: "creativeType",
        value: (data) => data.creativeType,
      }),
      ...this.dspColumns.metricsColumns<DspCreativeStats>((params) =>
        params.node.isRowPinned() || !params.data
          ? undefined
          : this.previousCreativeStatsGridData.get(params.data.creativeId!),
      ),
      {
        ...actionColumnProperties<DspCreativeStats, string>(),
        cellRendererSelector: (params) => {
          const title = params.node.isRowPinned()
            ? this.translocoService.translate("dsp-stats.dsp_creative_stats_graph_total")
            : this.translocoService.translate("dsp-stats.dsp_creative_stats_graph") +
              " - " +
              params.data?.dspCreativeName;
          const btns: ActionButton[] = [
            {
              icon: "chartLine",
              tooltip: params.node.isRowPinned()
                ? this.translocoService.translate("dsp-stats.display_dsp_creative_stats_graph_total")
                : this.translocoService.translate("dsp-stats.display_dsp_creative_stats_graph"),
              onClick: (params: ICellRendererParams) => {
                const creativeIds = getFilteredLeafNodes(params.node, this.creativeStatsGrid.api).map(
                  (n) => n.data.creativeId as string,
                );
                this.openChartModal(creativeIds, title, this.creativeStats$);
              },
            },
            {
              icon: "list",
              tooltip: this.translocoService.translate("dsp-stats.display_dsp_creative_stats_details"),
              onClick: (params: ICellRendererParams) => {
                const nodes = getFilteredLeafNodes(params.node, this.creativeStatsGrid.api);
                const lineItemsData = nodes.flatMap((n) => n.data.lineItemStats as DspStats[]);
                const title =
                  nodes.length > 1
                    ? this.translocoService.translate("dsp-stats.line_item_details")
                    : this.translocoService.translate("dsp-stats.line_item_details_creative", {
                        creative: nodes[0].data.dspCreativeName ?? "unknown",
                      });
                this.openLineItemDetailsModal(title, lineItemsData);
              },
            },
          ];
          return {
            component: ActionButtonsComponent,
            params: {
              actionButtons: btns,
            },
          };
        },
      },
      this.dspColumns.currencyColumn<DspCreativeStats>(),
    ],
    getRowId: (row) => row.data.creativeId ?? "",
  };

  private readonly dspCampaigns$ = this.dspAdvertiser$.pipe(
    switchMap((dspAdvertiser) => {
      if (dspAdvertiser) {
        return this.dspService.getDspCampaigns(dspAdvertiser.dspAdvertiserId);
      }
      return of([]);
    }),
    shareReplay(1),
  );
  dspCampaigns = toSignal(this.dspCampaigns$);

  private readonly dspCreatives$ = this.dspAdvertiser$.pipe(
    switchMap((dspAdvertiser) => {
      if (dspAdvertiser) {
        return this.dspService.getDspCreatives(dspAdvertiser.dspAdvertiserId);
      }
      return of([]);
    }),
    shareReplay(1),
  );
  private readonly dspCreatives = toSignal(this.dspCreatives$);

  readonly filters = computed<Filter<DspStatsFilter>[]>(() => [
    this.compactFilterService.getFilterWithOptions(FilterCategory.DspCampaign, this.dspCampaigns() ?? [], {
      unique: true,
    }),
    this.compactFilterService.getFilterWithOptions(FilterCategory.DspCreative, this.dspCreatives() ?? [], {
      unique: true,
    }),
  ]);

  readonly previousData = signal<DspStats[]>([]);
  readonly chartMetrics = signal<Metric<DspStats>[]>([DSP_SALES, DSP_COST]);
  readonly locale = toSignal(this.authService.userLocale$, { requireSync: true });
  readonly currency = toSignal(this.userSelectionService.selectedCurrency$, { requireSync: true });
  readonly dateAggregation = signal<DateAggregation>(DateAggregation.daily); // Date aggregation for the chart
  private readonly dateRange = toSignal(this.userSelectionService.selectedDateRange$, { requireSync: true });
  private readonly periodComparison = toSignal(this.userSelectionService.periodComparison$, { requireSync: true });

  public readonly chartConfig = computed<ChartConfig<DspStats, "line">>(() => ({
    type: "line",
    aspectRatio: 3,
    aggFunctions: [sumDspStats],
    metrics: this.chartMetrics(),
    dateAggregation: this.dateAggregation(),
    dateRange: this.dateRange(),
    previousPeriodData: this.previousData(),
    previousPeriod: this.periodComparison()?.period,
    metricsOnSameScale: [[DSP_SALES, DSP_COST]],
  }));

  constructor() {
    effect(() => {
      this.userSelectionService.setUserChartDisplayedPreference(this.localstorageKey, this.globalChartDisplay());
    });
  }

  ngOnInit(): void {
    this.dspAdvertiser$
      .pipe(
        untilDestroyed(this),
        switchMap((dspAdvertiser) => {
          if (dspAdvertiser) {
            return this.dspService.getDspAdgroups(dspAdvertiser.dspAdvertiserId);
          }
          return of([]);
        }),
      )
      .subscribe({
        next: (adgroups) => {
          this.dspAdgroups.clear();
          for (const adgroup of adgroups) {
            if (!adgroup.adGroupId) {
              continue;
            }
            this.dspAdgroups.set(adgroup.adGroupId, adgroup);
          }
        },
        error: (error) => {
          this.toastrService.error(this.translocoService.translate("dsp-stats.error_loading_dsp_adgroups"));
        },
      });

    combineLatest([
      this.accountSelectionService.singleAccountMarketplaceSelection$,
      this.dspAdvertiser$.pipe(filter((a) => a !== undefined)),
      this.userSelectionService.selectedCurrency$,
      this.userSelectionService.dateRange$,
    ])
      .pipe(
        tap(() => {
          this.dataLoading.set(true);
        }),
        untilDestroyed(this),
        switchMap(([accountMarketplace, dspAdvertiser, currency, dateRange]) => {
          return combineLatest([
            this.statsService.getDspLineItemsStats(
              accountMarketplace.accountId,
              accountMarketplace.marketplace,
              dspAdvertiser.dspAdvertiserId,
              currency,
              dateRange[0],
              dateRange[1],
            ),
            this.userSelectionService.periodComparison$.pipe(
              switchMap((periodCompararison) => {
                if (periodCompararison && periodCompararison.period && periodCompararison.period.length == 2) {
                  return this.statsService.getDspLineItemsStats(
                    accountMarketplace.accountId,
                    accountMarketplace.marketplace,
                    dspAdvertiser.dspAdvertiserId,
                    currency,
                    periodCompararison.period[0],
                    periodCompararison.period[1],
                  );
                }
                return of([]);
              }),
            ),
            this.dspCampaigns$,
            this.dspCreatives$,
            this.filter$,
          ]);
        }),
        tap({
          next: () => this.dataLoading.set(false),
          error: () => this.dataLoading.set(false),
        }),
      )
      .subscribe(([stats, previousStats, dspCampaigns, dspCreatives, filter]) => {
        let totalData = emptyDspStats();
        let previousTotalData = emptyDspStats();
        const globalDatasetData: DspStats[] = [];
        const previousData: DspStats[] = [];
        // build campaign and creative stats
        const statsPerCampaign = new Map<string, DspCampaignStats>();
        const campaignStats = new Map<string, ChartData<DspStats>>();
        const creativeStats = new Map<string, ChartData<DspStats>>();
        const statsPerCreative = new Map<string, DspCreativeStats>();
        for (const stat of stats) {
          if (filter.dspOrderIds && stat.orderId && !filter.dspOrderIds.includes(stat.orderId)) {
            continue;
          }
          if (filter.dspCreativeIds && stat.creativeId && !filter.dspCreativeIds.includes(stat.creativeId)) {
            continue;
          }
          if (isEmptyDspStats(stat)) {
            continue;
          }
          totalData = sumDspStats(totalData, stat);
          globalDatasetData.push(stat);

          if (stat.orderId) {
            const existing = statsPerCampaign.get(stat.orderId);
            if (existing) {
              statsPerCampaign.set(stat.orderId, { ...existing, ...sumDspStats(existing, stat) });
              statsPerCampaign.get(stat.orderId)!.lineItemStats.push(stat);
              const chartData = campaignStats.get(stat.orderId)!;
              chartData.data.push(stat);
              chartData.totalData = statsPerCampaign.get(stat.orderId)!;
            } else {
              const dspCampaign = dspCampaigns.find((c) => c.campaignId == stat.orderId);
              if (!dspCampaign) {
                statsPerCampaign.set(stat.orderId, { ...stat, lineItemStats: [{ ...stat }] });
              } else {
                statsPerCampaign.set(stat.orderId, {
                  ...stat,
                  dspCampaignName: dspCampaign.name,
                  state: dspCampaign.state,
                  deliveryStatus: dspCampaign.deliveryStatus,
                  lineItemStats: [{ ...stat }],
                });
              }
              campaignStats.set(stat.orderId, {
                data: [stat],
                totalData: { ...stat },
              });
            }
          }
          if (stat.creativeId) {
            const existing = statsPerCreative.get(stat.creativeId);
            if (existing) {
              statsPerCreative.set(stat.creativeId, { ...existing, ...sumDspStats(existing, stat) });
              statsPerCreative.get(stat.creativeId)!.lineItemStats.push(stat);
              const chartData = creativeStats.get(stat.creativeId)!;
              chartData.data.push(stat);
              chartData.totalData = statsPerCreative.get(stat.creativeId)!;
            } else {
              const dspCreative = dspCreatives.find((c) => c.adCreativeId == stat.creativeId);
              statsPerCreative.set(stat.creativeId, {
                ...stat,
                dspCreativeName: dspCreative?.name,
                type: dspCreative?.adCreativeFormatProperties?.adCreativeFormatType,
                lineItemStats: [{ ...stat }],
              });

              creativeStats.set(stat.creativeId, {
                data: [stat],
                totalData: { ...stat },
              });
            }
          }
        }
        this.previousCampaignStatsGridData = new Map<string, DspStats>();
        this.previousCreativeStatsGridData = new Map<string, DspStats>();
        for (const stat of previousStats) {
          if (filter.dspOrderIds && stat.orderId && !filter.dspOrderIds.includes(stat.orderId)) {
            continue;
          }
          if (filter.dspCreativeIds && stat.creativeId && !filter.dspCreativeIds.includes(stat.creativeId)) {
            continue;
          }
          previousTotalData = sumDspStats(previousTotalData, stat);
          previousData.push(stat);

          if (stat.orderId) {
            const existing = this.previousCampaignStatsGridData.get(stat.orderId);
            if (existing) {
              this.previousCampaignStatsGridData.set(stat.orderId, sumDspStats(existing, stat));
            } else {
              this.previousCampaignStatsGridData.set(stat.orderId, { ...stat });
            }
            const chartData = campaignStats.get(stat.orderId)!;
            if (chartData && chartData.previousData) {
              chartData.previousData.push(stat);
              chartData.totalPreviousData = this.previousCampaignStatsGridData.get(stat.orderId)!;
            } else if (chartData) {
              chartData.previousData = [stat];
              chartData.totalPreviousData = { ...stat };
            }
          }
          if (stat.creativeId) {
            const existing = this.previousCreativeStatsGridData.get(stat.creativeId);
            if (existing) {
              this.previousCreativeStatsGridData.set(stat.creativeId, sumDspStats(existing, stat));
            } else {
              this.previousCreativeStatsGridData.set(stat.creativeId, { ...stat });
            }
            const chartData = creativeStats.get(stat.creativeId)!;
            if (chartData && chartData.previousData) {
              chartData.previousData.push(stat);
              chartData.totalPreviousData = this.previousCreativeStatsGridData.get(stat.creativeId)!;
            } else if (chartData) {
              chartData.previousData = [stat];
              chartData.totalPreviousData = { ...stat };
            }
          }
        }

        this.totalData.set(totalData);
        this.previousTotalData.set(previousTotalData);
        this.globalDatasetData.set(globalDatasetData);
        this.previousData.set(previousData);
        this.campaignStatsGridData.set(Array.from(statsPerCampaign.values()));
        this.creativeStatsGridData.set(Array.from(statsPerCreative.values()));
        this.campaignStats$.next(campaignStats);
        this.creativeStats$.next(creativeStats);
        this.campaignStatsGrid.api?.refreshCells({ force: true });
        this.creativeStatsGrid.api?.refreshCells({ force: true });
      });

    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(
        untilDestroyed(this),
        tap((accountMarketplace) => {
          this.loadingService.loadingOn();
          this.accountMarketplace = accountMarketplace;
        }),
        switchMap((accountMarketplace) => {
          if (accountMarketplace.dspAdvertiserId) {
            return this.accountMarketplaceService.getDspAdvertiser(accountMarketplace.dspAdvertiserId).pipe(
              catchError((error) => {
                this.toastrService.error(this.translocoService.translate("dsp-stats.error_loading_dsp_advertiser"));
                return of(undefined);
              }),
            );
          }
          return of(undefined);
        }),
      )
      .subscribe((dspAdvertiser) => {
        this.dspAdvertiser$.next(dspAdvertiser);
        this.loadingService.loadingOff();
      });
  }

  toggleGlobalChartDisplay(display: boolean) {
    this.globalChartDisplay.set(display);
  }

  exportCampaignGridCsv(): void {
    if (!this.accountMarketplace) {
      return;
    }
    const fileName = getCsvFileName(
      "dsp_campaign_stats_",
      this.accountMarketplace.accountGroupName,
      this.accountMarketplace.marketplace,
      this.userSelectionService.getDateRangeStr(),
    );
    this.dspColumns.exportCsv(this.campaignStatsGrid.api, {
      fileName,
      columnHeaders: {
        dspCampaignName: "dsp-stats.order",
        state: "dsp-stats.state",
        deliveryStatus: "dsp-stats.deliveryStatus",
      },
    });
  }

  exportCreativeGridCsv(): void {
    if (!this.accountMarketplace) {
      return;
    }
    const fileName = getCsvFileName(
      "dsp_creative_stats_",
      this.accountMarketplace.accountGroupName,
      this.accountMarketplace.marketplace,
      this.userSelectionService.getDateRangeStr(),
    );
    this.dspColumns.exportCsv(this.creativeStatsGrid.api, {
      fileName,
      columnHeaders: { dspCreativeName: "dsp-stats.creative", creativeType: "dsp-stats.creativeType" },
    });
  }

  restoreCampaignGridColumns() {
    restoreDefaultColumns("dsp_campaign_stats", this.campaignStatsGrid.api);
  }

  restoreCreativeGridColumns() {
    restoreDefaultColumns("dsp_creative_stats", this.creativeStatsGrid.api);
  }

  applyFilter(filters: Filter<DspStatsFilter>[]) {
    if (filters.length === 0) {
      this.filter$.next({});
      return;
    }
    const filter: { dspOrderIds?: string[]; dspCreativeIds?: string[] } = {};
    for (const filterVal of filters) {
      if (filterVal.values.length === 0) {
        continue;
      }
      if (filterVal.type === FilterCategory.DspCampaign) {
        filter.dspOrderIds = filterVal.values.map((v) => (v as DspCampaign).campaignId).filter((v): v is string => !!v);
      }
      if (filterVal.type === FilterCategory.DspCreative) {
        filter.dspCreativeIds = filterVal.values
          .map((v) => (v as DspCreative).adCreativeId)
          .filter((v): v is string => !!v);
      }
    }
    this.filter$.next(filter);
  }

  private openChartModal(ids: string[], title: string, chartData$: Observable<Map<string, ChartData<DspStats>>>) {
    const modalOption: ModalOptions<ChartModalInputData<DspStats>> = {
      modalTitle: title,
      data: {
        locale: this.locale(),
        currency: this.currency(),
        dataSetConfiguration: {
          aggregationFunction: [sumDspStats],
          metricsOnSameScale: [[DSP_SALES, DSP_COST]],
        },
        chartData$: chartData$.pipe(
          map((d) => {
            let totalData = emptyDspStats();
            let totalPreviousData = emptyDspStats();
            const data: DspStats[] = [];
            const previousData: DspStats[] = [];
            for (const id of ids) {
              const node = d.get(id);
              if (node) {
                totalData = sumDspStats(totalData, node.totalData);
                if (node.totalPreviousData) {
                  totalPreviousData = sumDspStats(totalPreviousData, node.totalPreviousData);
                }
                data.push(...node.data);
                if (node.previousData) {
                  previousData.push(...node.previousData);
                }
              }
            }
            return { data, totalData, totalPreviousData, previousData };
          }),
        ) as Observable<ChartData<DspStats>>,
        metrics: this.dspMetrics,
        selectedMetrics: this.chartMetrics(),
        localStorageKey: this.localstorageKey,
      },
    };
    this.modalService.openModal<ChartModalInputData<DspStats>, void>(ChartModalComponent, modalOption);
  }

  private openLineItemDetailsModal(title: string, lineItemsData: DspStats[]) {
    this.modalService.openModal<DspStatsLineItemDetailsModalData, void>(DspStatsLineItemDetailsModalComponent, {
      modalTitle: title,
      data: <DspStatsLineItemDetailsModalData>{
        lineItemsData,
        lineItems: this.dspAdgroups,
      },
    });
  }
}

type DspCampaignStats = DspStats & {
  dspCampaignName?: string;
  state?: DspCampaignStateEnum;
  deliveryStatus?: DspDeliveryStatus;
  lineItemStats: DspStats[];
};

type DspStatsFilter = FilterCategory.DspCampaign | FilterCategory.DspCreative;
